import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { PatientMinimum } from '../../model/patient';

import { Patient } from '../../models/patient/patient';
import {
  PatientContactPreferencesReq,
} from '../../models/patient/patient-contact-preferences-req';
import { PatientAddress } from '../../models/patient/patientAddress';
import {
  PatientEmergencyContactInfo,
} from '../../models/patient/patientEmergencyContactInfo';
import { PatientEmployerInfo } from '../../models/patient/patientEmployerInfo';
import { EMPSubscriptionCheckout, GetEmployerSubscription, GetPatientSubscription, PatientFavoriteRequest, PatientSubscription, patientSubscriptionCheckout } from '../../models/patient/patientFavoriteRequest';
import { MedicineModel, PatientHealthHistory } from '../../models/patient/PatientHealthHistory';
import { BaseService } from '../shared/base.service';
import { getDefaultEventEnd } from 'angular-calendar/modules/common/util';

@Injectable({
  providedIn: "root",
})
export class PatientService {
  isConsentForm: boolean = false;

  constructor(private baseService: BaseService) { }

  getCurrentPatientDetails(): Observable<Patient> {
    return this.baseService.get<Patient>("Patients/GetCurrentPatientDetails");
  }

  getPatientDetailsByUserId(userId: number): Observable<Patient> {
    return this.baseService.get<Patient>(`Patients/GetPatientDetailsByUserId?userid=${userId}`);
  }

  getPatientDetailsById(id: number): Observable<Patient> {
    return this.baseService.get<Patient>(
      `Patients/GetPatientDetailsById?id=${id}`
    );
  }

  getPatientDetailsByName(name: string): Observable<Patient[]> {
    return this.baseService.get<Patient[]>(
      `Patients/GetPatientDetailsByName?name=${name}`
    );
  }

  getPatientDetailsByNameOrMobile(search: string): Observable<Patient[]> {
    return this.baseService.get<Patient[]>(
      `Patients/GetPatientDetailsByNameOrPhoneNumber?search=${search}`
    );
  }

  getPatientDetailsByPractitionerAndName(
    npi: string,
    name: string
  ): Observable<Patient[]> {
    return this.baseService.get<Patient[]>(
      `Patients/GetPractitionerPatientDetailsByName?npi=${npi}&name=${name}`
    );
  }

  updatePatientDetails(patientProfile: Patient): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientDetails`,
      patientProfile
    );
  }

  uploadPatientProfilePicture(payload: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Patients/UploadPatientProfilePicture`,
      payload
    );
  }

  uploadPatientEditPicture(patientId, file): Observable<any> {
    const pictureForm = new FormData();
    pictureForm.append("ImageSource", file);
    pictureForm.append("PatientId", patientId);

    return this.baseService.upload<any>(
      `Patients/UploadPatientProfilePicture`,
      pictureForm
    );
  }

  updatePatientDrivingLicenseDetails(payload: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Patients/UpdatePatientDrivingLicenseDetails`,
      payload
    );
  }
  updatePatientDrivingLicense(payload: FormData): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientDrivingLicenseDetails`,
      payload
    );
  }

  updatePatientChildrenDetails(childrenGuardian: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Patients/UpdatePatientChildrenDetails`,
      childrenGuardian
    );
  }

  updatePatientAdvancedDirective(advancedDirective: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Patients/UpdatePatientAdvancedDirective`,
      advancedDirective
    );
  }

  updatePatientAddressDetails(addresses: PatientAddress[]): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientAddressDetails`,
      addresses
    );
  }

  updatePatientEmergencyContactInfo(
    emergencyContact: PatientEmergencyContactInfo
  ): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientEmergencyContactInfo`,
      emergencyContact
    );
  }

  updatePatientContactPreferences(
    contactPref: PatientContactPreferencesReq
  ): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientContactPreferences`,
      contactPref
    );
  }

  updatePatientPharmacyPreferences(data): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientPharmacyDetails`,
      data
    );
  }

  getPatientInsuranceDetailsByPatientId(patientId: number): Observable<any> {
    return this.baseService.get<Patient>(
      `Patients/GetPatientInsuranceDetailsByPatientId?patientId=${patientId}`
    );
  }
  getBenefitsFromAvaility(patientId: number, physiciannpi, isRefresh = false, insuranceId: number = 0): Observable<any> {
    return this.baseService.get<any>(
      `InsurancePlan/GetBenefitsFromAvaility?patientId=${patientId}&providerNpi=${physiciannpi}&isMobile=false&patientInsuranceId=${insuranceId}&isRefresh=${isRefresh}`
    );
  }

  updatePatientEmployerInfo(PatientEmployerInfo: PatientEmployerInfo): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientEmployerInfo`,
      PatientEmployerInfo
    );
  }

  updatePatientMilitaryStatus(data): Observable<any> {
    return this.baseService.post<any>(
      `Patients/UpdatePatientMilitaryStatus`,
      data
    );
  }

  deletePatientEmergencyContactInfo(
    patientId: number,
    id: number
  ): Observable<any> {
    return this.baseService.delete<any>(
      `Patients/DeletePatientEmergencyContactInfo?patientId=${patientId}&id=${id}`
    );
  }

  deletePatientChild(patientId: number, id: number): Observable<any> {
    return this.baseService.delete<any>(
      `Patients/DeletePatientChild?patientId=${patientId}&id=${id}`
    );
  }

  deletePharmacyPreference(patientId: number, npi: string): Observable<any> {
    return this.baseService.delete<any>(
      `Patients/DeletePatientPharmacyPreference?patientId=${patientId}&npi=${npi}`
    );
  }
  deletePatientInsurance(patientInsuranceId: number): Observable<any> {
    return this.baseService.delete<any>(
      `Patients/DeletePatientInsurance?patientInsuranceId=${patientInsuranceId}`
    );
  }

  addPatientInsurance(patientInsurance: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Patients/AddPatientInsurance`,
      patientInsurance
    );
  }
  updatePatientInsurance(patientInsurance: FormData): Observable<any> {
    return this.baseService.upload<any>(
      `Patients/UpdatePatientInsurance`,
      patientInsurance
    );
  }
  deletePatientMedication(id: number): Observable<any> {
    return this.baseService.delete<any>(
      `Medication/${id}`
    );
  }
  deletePatientAllergy(patientId: number, id: number): Observable<any> {
    return this.baseService.delete<any>(
      `Patients/DeletePatientAllergy?patientId=${patientId}&id=${id}`
    );
  }
  getHealthTypeAndSubType(typeId: number = 0): Observable<any> {
    return this.baseService.get<any>(
      `PatientHealthHistory/GetHealthTypes?typeId=${typeId}`
    );
  }
  getHealthHistoryQuestions(typeId: number = 0): Observable<any> {
    return this.baseService.get<any>(
      `PatientHealthHistory/GetHealthHistoryQuestions?healthTypeId =${typeId}`
    );
  }
  getPatientHealthHistoryData(pPatientId: number): Observable<any> {
    return this.baseService.get<any>(
      `PatientHealthHistory/GetPatientHealthHistory?pPatientId=${pPatientId}`
    );
  }
  savePatientHealthHistoryData(data: PatientHealthHistory[]): Observable<any> {
    return this.baseService.post<any>('PatientHealthHistory/UpdatePatientHealthHistory', data);
  }
  uploadPatientMedicalHistoryReport(data: FormData): Observable<any> {
    return this.baseService.upload<any>('PatientHealthHistory/UploadPatientReportFile', data);
  }
  CreateMedicineOrSuppliment(data: MedicineModel): Observable<any> {
    return this.baseService.post<any>('Medicine/Add', data);
  }

  getPatientFavoritesById(patientId: number): Observable<any> {
    return this.baseService.get<any>('Patients/GetPatientFavoritesByPatientId?patientId=' + patientId);
  }
  setOrRemovePatientFavorites(data: PatientFavoriteRequest): Observable<any> {
    return this.baseService.post<any>('Patients/SetPatientFavorite', data);
  }
  createMinimumnPatient(patient: PatientMinimum): Observable<Patient> {
    return this.baseService.post<Patient>('Patients/CreateMinimum', patient)
  }
    updatePatientHealthSubscription(patientOrgId:number,employerOrgId:number,subscriptionType:number,activationType: string):Observable<any>{
      const patientSubscription: PatientSubscription = {
        patientId: patientOrgId,
        employerId:employerOrgId,
        subscriptionTypeId: subscriptionType,
        activationType: activationType,
      };
      return this.baseService.post<any>('Patients/UpdatePatientHealthSubscription', patientSubscription);
    }
    getPatientSubscriptionDetailsByPatientId(patientId: GetPatientSubscription): Observable<any> {
      return this.baseService.get<any>(`Patients/GetPatientHealthSubscription?patientId=${patientId}`);
    }
    // deleteMemberPatients(userId: number): Observable<any> {
    //   return this.baseService.delete<any>(
    //     `Patients/DeleteMemberPatients?userId=${userId}`
    //   );
    // }
    deleteMemberPatients(userId: number): Observable<any> {
      return this.baseService.delete<any>(
        `Agency/DeleteMemberPatients/${userId}`
      );
    }
    getPatientSubscriptionCheckOutInfomation(patientId:number,employerId:number,subscriptionTypeId:number,):Observable<any>{
      const patientSubscriptionCheckout: patientSubscriptionCheckout = {
        patientId: patientId,
        employerId:employerId,
        subscriptionTypeId: subscriptionTypeId,
      };
      return this.baseService.post<any>('Patients/GetPatientSubscriptionCheckOutInformation', patientSubscriptionCheckout);
    }
    // getTeledocSSOSignIn(): Observable<any> {
    //   return this.baseService.get<any>(
    //    'HealthSubscriptionPlan/TeledocSSOSignIn'
    //   );
    // }
    getTeledocSSOSignIn(): Observable<Blob> {
      return this.baseService.getBlob('HealthSubscriptionPlan/TeledocSSOSignIn');
    }
    
    getSSOSignIn(plan: string): Observable<any> {
      return this.baseService.post(
        `HealthSubscriptionPlan/SSOSignIn?plan=${plan}`,
        {}
      );
    }
    getPatientSubscriptionTypes(): Observable<any> {
      return this.baseService.get<any>('Patients/GetPatientSubscriptionTypes');
    }

}
